import React from 'react';
import {Typography} from '@mui/material';
import {FieldInputProps} from 'formik';
import {sortBy} from 'lodash-es';
import {getCurrencyFloat, getCurrencyString} from '../../../utils/currencyFormatter';
import {withdrawalFieldsType} from './withdrawalFields';
import {Pot} from '../../../schema-types/Pot';
import {getDefaultAccountId, isBankAccount} from '../../../utils/potsFilter';
import {
  changeWithdrawalSourceFactory,
  changeWithdrawalDestinationFactory,
  loadSavedWithdrawal,
} from '../hooks/withdrawalHooks';

export {
  changeWithdrawalSourceFactory,
  changeWithdrawalDestinationFactory,
  loadSavedWithdrawal,
};

export const getWithdrawalMessageFactory = ({
  withdraw,
}: {
  withdraw: withdrawalFieldsType | undefined;
}) => {
  return (isSelected: boolean) => {
    if (!isSelected) {
      return null;
    }
    return (
      <Typography>
        <strong data-testid="withdraw-message-id">{` - Withdrawal amount ${getCurrencyString(
          Number(withdraw?.source?.singleClientAmount || 0) +
            Number(withdraw?.source.recurringClientAmount || 0)
        )}`}</strong>
      </Typography>
    );
  };
};
export const withdrawalSources = ({
  sourcePots,
  isSppDa,
  reviewId,
}: {
  sourcePots: Pot[];
  isSppDa: boolean;
  reviewId: string;
}): Pot[] => {
  return sourcePots.filter((el) => {
    // Preventing empty accounts from being listed as sources for withdrawals
    // as that would be non-sensical
    if (el.totalValue === '0.00' || !el.totalValue) {
      return false;
    }
    // For SPP, only withdrawal from a plan proposed in OneView, but not submitted, OR from a plan that exists in Curo.
    if (isSppDa) {
      if (el.review?.id === reviewId) {
        return true;
      }
      if (el.curoId) {
        return true;
      }
      return false;
    }
    return true;
  });
};
export const getWithdrawalSortedDestinations = ({
  sourcePots,
  destinationPots,
  withdrawSourceObj,
}: {
  sourcePots: Pot[];
  destinationPots: Pot[];
  withdrawSourceObj: FieldInputProps<withdrawalFieldsType['source'] | undefined>;
}) => {
  const defaultId = getDefaultAccountId(sourcePots, withdrawSourceObj.value?.id || '');
  const defaultAccount = destinationPots?.filter((x) => x.id === defaultId) || [];
  const authorisedAccounts =
    destinationPots?.filter((x) => x.id !== defaultId && x.isAuthorised) || [];
  const unauthorisedAccounts =
    destinationPots?.filter((x) => x.id !== defaultId && !x.isAuthorised) || [];
  return [...defaultAccount, ...authorisedAccounts, ...unauthorisedAccounts];
};
export const withdrawalDestinations = ({
  sortedDestinations,
  withdrawSourceObj,
}: {
  sortedDestinations: Pot[];
  withdrawSourceObj: FieldInputProps<withdrawalFieldsType['source'] | undefined>;
}): Pot[] => {
  return sortBy(sortedDestinations, (a) => {
    return a.productIsBank || isBankAccount(a) ? -1 : 1;
  }).filter((x) => x.id !== withdrawSourceObj.value?.id);
};
